import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const threePillars = [
  {
    keyword: '讲深',
    subtitle: '理论逻辑',
    desc: '把现实问题背后的理论逻辑讲出来，概念、制度、历史和现实之间的关系讲明白',
    icon: 'ri-arrow-down-circle-line',
  },
  {
    keyword: '讲透',
    subtitle: '体系贯通',
    desc: '让学生通过案例、问题和活动真正进入思考，案例支撑理论，互动推动理解',
    icon: 'ri-arrow-left-right-line',
  },
  {
    keyword: '讲活',
    subtitle: '生动共鸣',
    desc: '价值引导自然进入学生理解，不是灌输而是唤醒，从认知走向认同',
    icon: 'ri-heart-pulse-line',
  },
];

export default function P10GoodLesson({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-16 left-1/3 w-96 h-96 rounded-full bg-primary-100/10 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-16 right-20 w-80 h-80 rounded-full bg-accent-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-10 md:mb-12">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            一堂好课
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            把道理<span className="text-primary-600">讲深、讲透、讲活</span>
          </h2>
        </div>

        {/* Quote card */}
        <div className={`max-w-3xl mx-auto mb-10 p-6 md:p-8 rounded-2xl glass-strong border-primary-200/30 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-1 h-auto self-stretch rounded-full bg-primary-400/60" />
            <div>
              <i className="ri-double-quotes-l text-3xl text-primary-300/40 mb-2 block" />
              <p className="font-heading text-lg md:text-xl text-foreground-800 leading-relaxed font-semibold italic">
                "思政课的本质是讲道理，要注重方式方法，把道理讲深、讲透、讲活。"
              </p>
              <div className="mt-4 pt-4 border-t border-background-300/30">
                <p className="text-xs text-foreground-400 font-body">—— 习近平总书记在学校思想政治理论课教师座谈会上的重要讲话</p>
              </div>
            </div>
          </div>
        </div>

        {/* Three pillars */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {threePillars.map((pillar, i) => (
            <div
              key={pillar.keyword}
              className="group relative p-6 md:p-8 rounded-2xl glass-liquid border-primary-200/30 hover:border-primary-300/50 transition-all duration-500 hover:scale-[1.02]"
              style={{ transitionDelay: `${500 + i * 120}ms` }}
            >
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl glass-blue border-primary-200/40 flex items-center justify-center mb-5">
                  <i className={`${pillar.icon} text-2xl text-primary-600`} />
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {pillar.keyword}
                </h3>
                <p className="text-xs font-body font-semibold text-foreground-400 uppercase tracking-wider mb-4">{pillar.subtitle}</p>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">{pillar.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-8 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass border-white/50">
            <i className="ri-focus-2-line text-primary-500" />
            <span className="text-sm font-body text-foreground-600">AI不能只看教师有没有<strong className="text-primary-700">提到概念</strong>，还要判断道理有没有<strong className="text-primary-700">被展开</strong></span>
          </div>
        </div>
      </div>
    </div>
  );
}